import { jsPDF } from "jspdf";
import RobotoRegular from "../Asset/Font/RobotoRegular.ttf";
import RobotoItalics from "../Asset/Font/RobotoItalic.ttf";
import RobotoBold from "../Asset/Font/RobotoBold.ttf";
import RobotoBoldItalics from "../Asset/Font/RobotoBoldItalic.ttf";
import TangerineRegular from "../Asset/Font/TangerineRegular.ttf";
import TangerineBold from "../Asset/Font/TangerineBold.ttf";
import rehabMedicalLogo from "../Asset/Image/rehab_medical_logo.png?inline";
/**
 * Custom PDF class. This uses composition instead of inheritance has jsPDF does
 * not play nice if you try to extend it. Otherwise provides some defaults and
 * nice helper functions.
 */
export default class Pdf {
    static context;

    /**
     * @param {object} context The context.
     */
    static setContext(context) {
        Pdf.context = context;
    }

    constructor(data = {}, options = {}) {
        this.data = data;
        this.document = new jsPDF({
            unit: "pt",
            format: "letter",
            orientation: "portrait",
            putOnlyUsedFonts: true,
            ...options
        });
        this.fontLoadPromise = this.registerFonts();

        this.margin = {
            left: 36,
            top: 36,
            right: 36,
            bottom: 86,
        }

        this.header = {
            height: 0
        }

        this.settings = {};
    }

    /**
     * Registers custom fonts used by the PDF renderers.
     * Roboto stays embedded from the generated base64 file, while Tangerine
     * regular is converted from the bundled font asset before registration.
     */
    async registerFonts() {
        const robotoRegularBase64 = await this.convertFontToBase64(RobotoRegular);
        this.document.addFileToVFS("RobotoRegular.ttf", robotoRegularBase64);
        this.document.addFont("RobotoRegular.ttf", "Roboto", "normal");

        const robotoRobotoItalicsBase64 = await this.convertFontToBase64(RobotoItalics);
        this.document.addFileToVFS("RobotoItalics.ttf", robotoRobotoItalicsBase64);
        this.document.addFont("RobotoItalics.ttf", "Roboto", "italic");

        const robotoBoldBase64 = await this.convertFontToBase64(RobotoBold);
        this.document.addFileToVFS("RobotoBold.ttf", robotoBoldBase64);
        this.document.addFont("RobotoBold.ttf", "Roboto", "bold");

        const robotoBoldItalicsBase64 = await this.convertFontToBase64(RobotoBoldItalics);
        this.document.addFileToVFS("RobotoBoldItalics.ttf", robotoBoldItalicsBase64);
        this.document.addFont("RobotoBoldItalics.ttf", "Roboto", "bolditalic");

        const tangerineRegularBase64 = await this.convertFontToBase64(TangerineRegular);
        this.document.addFileToVFS("TangerineRegular.ttf", tangerineRegularBase64);
        this.document.addFont("TangerineRegular.ttf", "Tangerine", "normal");

        const tangerineBoldBase64 = await this.convertFontToBase64(TangerineBold);
        this.document.addFileToVFS("TangerineBold.ttf", tangerineBoldBase64);
        this.document.addFont("TangerineBold.ttf", "Tangerine", "bold");

        this.document.setFont("Roboto");
        this.document.setFontSize(12);
    }

    async convertFontToBase64(url) {
        const response = await fetch(url);
        const buffer = await response.arrayBuffer();
        let binary = "";
        let bytes = new Uint8Array(buffer);
        bytes.forEach((byte) => {
            binary += String.fromCharCode(byte);
        });
        return btoa(binary);
    }

    /**
     * Sanitizes the provided string value to change tabs into spaces,
     * carriage returns to new lines, and unicode characters out of range.
     * @param {string} value
     * @returns {string} sanitized string value
     */
    sanitizePdfText(value) {
        return String(value ?? "")
            // replaces most common Unicode bullet points with one that Roboto can read.
            .replace(/[\u2022\u25E6\u25AA\u25AB\u2043\u2219\u204C\u204D\u2981]/g, "\u25CF")
            // repalces tabulations with spaces.
            .replace(/\t/g, "    ")
            // replaces newline carriage returns with just newline.
            .replace(/\r\n/g, "\n")
            // repalces any other unicode character that may not be standard.
            .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "");
    }

    /**
     * Adds a table to the PDF document, including a logo and a title.
     *
     * @param {Object} options - Configuration options for the header.
     * @param {string} options.title - The title text to display in the header.
     */
    renderHeader(options) {
        this.saveSettings();
        // Set the logo dimensions = original size then scaled down to an appropriate size.
        const logoWidth = 628 / 4;
        const logoHeight = 149 / 4;

        const logoX = this.margin.left;
        const logoY = this.margin.top;

        this.document.addImage(
            rehabMedicalLogo,
            'PNG',
            logoX,
            logoY,
            logoWidth,
            logoHeight
        );

        // Title
        this.document.setFontSize(14);
        this.document.setFont(undefined, "bold");
        const textDimensions = this.document.getTextDimensions(options.title);

        const title_x = this.document.getPageWidth() - this.margin.left;
        const title_y = logoX + (logoHeight / 2) - (textDimensions.h / 2);

        this.document.text(
            options.title,
            title_x,
            title_y,
            {
                baseline: "top",
                align: "right"
            }
        );

        // Set the starting position for anything that cares about the header to
        // the header plus some margin.
        this.header.height = logoHeight + 10;

        this.restoreSettings();
        return this.header.height;
    }

    /**
     * Adds a table to the PDF document, including a logo and a title.
     *
     * @param {Object} options - Configuration options for the header.
     * @param {string} options.title - The title text to display in the header.
     */
    renderSubsequentHeader(options) {
        this.saveSettings();
        // Set the logo dimensions = original size then scaled down to an appropriate size.
        const logoWidth = 628 / 6;
        const logoHeight = 149 / 6;

        const logoX = this.margin.left;
        const logoY = this.margin.top;

        this.document.addImage(
            rehabMedicalLogo,
            'PNG',
            logoX,
            logoY,
            logoWidth,
            logoHeight
        );

        // Title
        this.document.setFontSize(14);
        this.document.setFont(undefined, "bold");
        const textDimensions = this.document.getTextDimensions(options.title);

        const title_x = this.document.getPageWidth() - this.margin.left;
        const title_y = logoX + (logoHeight / 2) - (textDimensions.h / 2);

        this.document.text(
            options.title,
            title_x,
            title_y,
            {
                baseline: "top",
                align: "right"
            }
        );

        // Set the starting position for anything that cares about the header to
        // the header plus some margin.
        this.header.height = logoHeight + 10;

        this.restoreSettings();
        return this.header.height;
    }

    /**
     * Renders an input in the PDF document.
     * @param {string} label - The label to render above the input line.
     * @param {string} data - The value to render in the input area.
     * @param {number} x - The x-coordinate for the input.
     * @param {number} y - The baseline y-coordinate for the input line.
     * @param {number} width - The width of the input line.
     * @param {Object} options - Rendering options.
     * @param {string} options.align - Text alignment for the input value.
     * @param {number} options.fontSize - Shared fallback font size for label and value.
     * @param {string} options.fontStyle - Shared fallback font style for label and value.
     * @param {string} options.labelFont - Font family for the label text.
     * @param {number} options.labelFontSize - Font size for the label text.
     * @param {string} options.labelFontStyle - Font style for the label text.
     * @param {string} options.valueFont - Font family for the input value.
     * @param {number} options.valueFontSize - Font size for the input value.
     * @param {string} options.valueFontStyle - Font style for the input value.
     * @param {string} options.title - Header title used when a page break occurs.
     * @param {boolean} options.renderHeader - Whether to render the header on a new page.
     */
    async renderInput(label, data, x, y, width, options = {}) {
        const currentFont = this.document.getFont();
        const currentFontSize = this.document.getFontSize();
        const defaultOptions = {
            align: "left",
            fontSize: currentFontSize,
            labelFontSize: currentFontSize,
            valueFontSize: currentFontSize,
            fontStyle: "normal",
            labelFont: currentFont.fontName,
            labelFontStyle: "normal",
            valueFont: currentFont.fontName,
            valueFontStyle: "normal",
            title: "",
            renderHeader: false
        };
        const opts = { ...defaultOptions, ...options };
        this.saveSettings();
        const sanitizedLabel = this.sanitizePdfText(label);
        const sanitizedData = this.sanitizePdfText(data);

        this.document.setFontSize(opts.labelFontSize);
        this.document.setFont(opts.labelFont, opts.labelFontStyle);
        this.document.text(sanitizedLabel, x, y + 15);
        const isSvg = data && typeof data === 'string' && data.trim().startsWith("<svg");
        if (isSvg) {
            // Convert SVG string to image and add to PDF
            const svgString = data;
            const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
            const src = URL.createObjectURL(svgBlob);
            const image = new Image();

            // Wait for the image to load
            await new Promise((resolve, reject) => {
                image.onload = () => resolve();
                image.onerror = (err) => reject(err);
                image.src = src;
            });

            // Define size
            const scaleFactor = 0.8;
            const scaledWidth = width * scaleFactor;
            const height = (image.height / image.width) * scaledWidth;

            // Convert SVG image to PNG via canvas (jsPDF can't directly handle SVG-loaded images)
            const canvas = document.createElement('canvas');
            canvas.width = image.width;
            canvas.height = image.height;
            const context = canvas.getContext('2d');
            context.drawImage(image, 0, 0);
            const pngDataUrl = canvas.toDataURL('image/png');

            // Draw line under the signature and anchor the signature image to it.
            this.document.setLineWidth(0.5);
            const signatureLineY = y + 5;
            const imageY = signatureLineY - height - 1;
            this.document.addImage(pngDataUrl, "PNG", x, imageY, scaledWidth, height);
            this.document.line(x, signatureLineY, x + scaledWidth, signatureLineY);

            URL.revokeObjectURL(src);

            return y + height + 10; // return new y after image
        }

        // ---------- REGULAR TEXT ----------
        const maxWidth =
            this.document.internal.pageSize.width -
            (this.margin.left + this.margin.right);

        const wrapOptions = {
            font: opts.valueFont,
            fontSize: opts.valueFontSize,
            fontStyle: opts.valueFontStyle
        };
        const wrappedLines = this.wrapText(sanitizedData, maxWidth, wrapOptions);
        const lines = Array.isArray(wrappedLines) ? wrappedLines : [sanitizedData];

        let lineHeight = opts.valueFontSize * 1.2;
        let pageHeight = this.document.internal.pageSize.height;
        let currentY = y;

        lines.forEach((line) => {
            if (currentY + lineHeight > pageHeight - this.margin.bottom) {
                currentY = this.renderAdditionalPage(currentY, {
                    renderHeader: opts.renderHeader,
                    title: opts.title
                });
                this.document.setFont(opts.labelFont, opts.labelFontStyle);
                this.document.setFontSize(opts.labelFontSize);
                this.document.text(sanitizedLabel, x, currentY + 15, {
                    align: opts.align
                });
            }
            this.document.setFont(opts.valueFont, opts.valueFontStyle);
            this.document.setFontSize(opts.valueFontSize);
            this.document.text(line, x, currentY, { align: opts.align });
            currentY += lineHeight;
        });

        this.document.setLineWidth(0.5);
        this.document.line(x, y + 5, x + width, y + 5);

        this.restoreSettings();
        return currentY;
    }

    /**
     * Renders text in the PDF document.
     * @param {string} label - The label for the text.
     * @param {string} data - The data to display.
     * @param {number} x - The x-coordinate.
     * @param {number} y - The y-coordinate.
     * @param {number} width - The horizontal offset from the label to the value.
     * @param {Object} options - Rendering options.
     * @param {string} options.align - Alignment for the value text.
     * @param {number} options.fontSize - Shared fallback font size for label and value.
     * @param {string} options.fontStyle - Shared fallback font style for label and value.
     * @param {string} options.labelFont - Font family for the label text.
     * @param {string} options.labelFontStyle - Font style for the label text.
     * @param {string} options.valueFont - Font family for the value text.
     * @param {string} options.valueFontStyle - Font style for the value text.
     * @param {string} options.title - Header title used when a page break occurs.
     * @param {boolean} options.renderHeader - Whether to render the header on a new page.
     */
    renderText(label, data, x, y, width, options = {}) {
        const currentFont = this.document.getFont();
        const currentFontSize = this.document.getFontSize();
        const defaultOptions = {
            align: undefined,
            fontSize: currentFontSize,
            labelFont: currentFont.fontName,
            labelFontStyle: "bold",
            valueFont: currentFont.fontName,
            valueFontStyle: "normal",
            title: "",
            renderHeader: false
        };
        const opts = { ...defaultOptions, ...options };
        this.saveSettings();
        const sanitizedLabel = this.sanitizePdfText(label);
        const sanitizedData = this.sanitizePdfText(data);
        const valueX = x + (width || 0);
        this.document.setFontSize(opts.fontSize);
        this.document.setFont(opts.labelFont, opts.labelFontStyle);
        const maxWidth = this.document.internal.pageSize.width - this.margin.right - valueX;
        const wrapOptions = {
            font: opts.valueFont,
            fontSize: opts.fontSize,
            fontStyle: opts.valueFontStyle
        };
        const wrappedLines = this.wrapText(sanitizedData, maxWidth, wrapOptions);
        const lines = Array.isArray(wrappedLines) ? wrappedLines : [sanitizedData];
        let lineHeight = opts.fontSize * 1.2;
        let pageHeight = this.document.internal.pageSize.height;
        let currentY = y;
        if (currentY + lineHeight > pageHeight - this.margin.bottom) {
            currentY = this.renderAdditionalPage(currentY, { renderHeader: opts.renderHeader, title: opts.title });
        }
        this.document.text(sanitizedLabel, x, currentY);
        lines.forEach((line) => {
            if (currentY + lineHeight > pageHeight - this.margin.bottom) {
                currentY = this.renderAdditionalPage(currentY, { renderHeader: opts.renderHeader, title: opts.title });
            }
            this.document.setFont(opts.valueFont, opts.valueFontStyle);
            this.document.setFontSize(opts.fontSize);
            this.document.text(line, valueX, currentY, { align: opts.align });
            currentY += lineHeight;
        });
        this.restoreSettings();
        return currentY;
    }

    /**
     * Splits text to fit within a given width.
     * @param {string} text - The text to wrap.
     * @param {number} maxWidth - The maximum width for the text.
     * @returns {Array<string>} Array of lines.
     */
    wrapText(text, maxWidth, options = {}) {
        const sanitizedText = this.sanitizePdfText(text);

        this.saveSettings();
        this.document.setFont(options.font, options.fontStyle);
        this.document.setFontSize(options.fontSize);
        const lines = this.document.splitTextToSize(sanitizedText, maxWidth);
        this.restoreSettings();

        return lines;
    }

    /**
     * Renders a section divider line in the PDF document.
     * @param {number} y - The y-coordinate for the divider.
     */
    renderSectionDivider(y) {
        this.saveSettings();

        const document = this.document;
        const margin = this.margin;

        document.setDrawColor(0, 0, 0);
        document.setLineWidth(0.5);
        document.line(margin.left, y, document.internal.pageSize.width - margin.right, y);

        this.restoreSettings();
    }

    /**
     * Renders additional pages in the pdf document that have a set style.
     * @param {*} y
     * @param {*} options
     * @returns
     */
    renderAdditionalPage(y, options = {}) {
        this.saveSettings();
        const defaultOptions = { renderHeader: false, title: "" };
        const opts = { ...defaultOptions, ...options };
        this.document.addPage();
        y = this.margin.top;
        if (opts.renderHeader) {
            y += this.renderSubsequentHeader({ title: opts.title });
        }
        y += 10;
        this.restoreSettings();
        return y;
    }

    /**
     * Renders a section title in the PDF document.
     * @param {string} title - The title text.
     * @param {number} x - The x-coordinate (optional).
     * @param {number} y - The y-coordinate.
     */
    renderSectionTitle(title, x, y) {
        this.saveSettings();

        const document = this.document;
        const margin = this.margin;
        document.setFont(undefined, "bold");
        document.setFontSize(11);
        document.text(title, x ?? margin.left, y);

        this.restoreSettings();
    }

    /**
     * Renders a table in the PDF document.
     * @param {Object} options - Configuration options for the table
     * @param {Array} options.columns - The columns for the table
     * @param {Array} options.columns.columnWidth - The width of the column (optional)
     * @param {Array} options.rows - The rows for the table
     * @param {Object} options.x - The x-coordinate for the table
     * @param {Object} options.y - The y-coordinate for the table
     * @param {boolean} options.renderHeader - Whether to render the header on new pages
     * @param {string} options.title - The title for the header on new pages
     * @param {number} options.logoScale - The scale for the logo on new pages
     * @returns {number} The new y-coordinate after rendering the table
     */
    renderTable(options) {
        this.saveSettings();

        const pageHeight = this.document.internal.pageSize.height;
        const maxTableHeight = pageHeight - this.header.height - 350; // Leave room for header and footer
        // Use options.x for left, and calculate width so table fits between margins
        const tableX = options.x ?? this.margin.left;
        const tableWidth = this.document.internal.pageSize.width - this.margin.left - this.margin.right;
        const baseRowHeight = 20;
        const headerHeight = 24;
        const cellPadding = 4;
        const renderHeader = options.renderHeader ?? true;
        const title = options.title ?? "";
        const blank = options.blank ?? false;

        // Calculate column widths so they sum to tableWidth
        let columnWidths;
        if (options.columns.every(col => col.columnWidth)) {
            // If all columns specify width, scale proportionally to fit tableWidth
            const total = options.columns.reduce((sum, c) => sum + c.columnWidth, 0);
            columnWidths = options.columns.map(c => c.columnWidth * tableWidth / total);
        } else {
            // Otherwise, use equal widths
            const defaultColumnWidth = tableWidth / options.columns.length;
            columnWidths = options.columns.map(() => defaultColumnWidth);
        }

        let currentY = options.y;
        let rowsProcessed = 0;
        let currentPage = 1;

        const drawTableColumns = (y) => {
            // Draw header row background (light gray)
            this.document.setFillColor(230, 230, 230);
            this.document.setDrawColor(0, 0, 0);
            this.document.setLineWidth(0.5);
            this.document.rect(tableX, y, tableWidth, headerHeight, 'FD');

            // Draw header text
            this.document.setFontSize(10);
            this.document.setFont(undefined, "bold");

            let currentX = tableX;
            options.columns.forEach((header, index) => {
                const headerAlign = header.align || "left";
                let headerX = currentX + cellPadding;
                const headerTextOptions = { baseline: 'middle' };

                if (headerAlign === "right") {
                    headerX = currentX + columnWidths[index] - cellPadding;
                    headerTextOptions.align = "right";
                } else if (headerAlign === "center") {
                    headerX = currentX + (columnWidths[index] / 2);
                    headerTextOptions.align = "center";
                }

                this.document.text(
                    header.label,
                    headerX,
                    y + headerHeight / 2 + 3,
                    headerTextOptions
                );
                // Draw header cell border
                this.document.rect(currentX, y, columnWidths[index], headerHeight);
                currentX += columnWidths[index];
            });
        };

        while (rowsProcessed < options.rows.length) {
            // Calculate remaining height on current page
            const availableHeight = maxTableHeight - (currentY - (this.header.height + 200));

            // Draw columns for this page
            drawTableColumns(currentY);
            currentY += headerHeight;

            // Draw rows that fit on this page
            this.document.setFont(undefined, "normal");

            let rowsFitOnPage = 0;
            let tempY = currentY;

            // First pass - calculate how many rows will fit by checking wrapped text heights
            for (let i = rowsProcessed; i < options.rows.length; i++) {
                const row = options.rows[i];
                let maxRowHeight = baseRowHeight;
                row.forEach((cellData, colIndex) => {
                    const textWidth = columnWidths[colIndex] - 2 * cellPadding;
                    const lines = this.wrapText(String(cellData), textWidth, {
                        font: this.document.getFont().fontName,
                        fontSize: 10,
                        fontStyle: "normal"
                    });
                    const cellHeight = lines.length * 12 + 8;
                    maxRowHeight = Math.max(maxRowHeight, cellHeight);
                });

                if (tempY + maxRowHeight <= currentY + availableHeight) {
                    rowsFitOnPage++;
                    tempY += maxRowHeight;
                } else {
                    break;
                }
            }

            // Second pass - actually draw the rows
            for (let i = 0; i < rowsFitOnPage; i++) {
                const row = options.rows[rowsProcessed + i];
                let rowHeight = baseRowHeight;
                const cellHeights = row.map((cellData, colIndex) => {
                    const textWidth = columnWidths[colIndex] - 2 * cellPadding;
                    const lines = this.wrapText(String(cellData), textWidth, {
                        font: this.document.getFont().fontName,
                        fontSize: 10,
                        fontStyle: "normal"
                    });
                    return blank ? 418 : lines.length * 12 + 8;
                });
                rowHeight = Math.max(rowHeight, ...cellHeights);

                // Draw row cells and borders
                let currentX = tableX;
                row.forEach((cellData, colIndex) => {
                    this.document.rect(currentX, currentY, columnWidths[colIndex], rowHeight);

                    const textWidth = columnWidths[colIndex] - 2 * cellPadding;
                    const lines = this.wrapText(String(cellData), textWidth, {
                        font: this.document.getFont().fontName,
                        fontSize: 10,
                        fontStyle: "normal"
                    });
                    const textHeight = lines.length * 12;
                    const verticalCenter = currentY + (rowHeight / 2);
                    const textY = verticalCenter - (textHeight / 2) + 2;
                    const columnAlign = options.columns[colIndex]?.align || "left";
                    let textX = currentX + cellPadding;
                    const textOptions = { baseline: 'top', maxWidth: textWidth };

                    if (columnAlign === "right") {
                        textX = currentX + columnWidths[colIndex] - cellPadding;
                        textOptions.align = "right";
                    } else if (columnAlign === "center") {
                        textX = currentX + (columnWidths[colIndex] / 2);
                        textOptions.align = "center";
                    }

                    this.document.text(
                        lines,
                        textX,
                        textY,
                        textOptions
                    );
                    currentX += columnWidths[colIndex];
                });
                currentY += rowHeight;
            }

            rowsProcessed += rowsFitOnPage;

            // If we have more rows to process, add a new page
            if (rowsProcessed < options.rows.length) {
                currentY = this.renderAdditionalPage(currentY, { renderHeader: renderHeader, title: title });
                currentPage++;
            }
        }
        this.restoreSettings();
        return currentY;
    }
    /**
     * Renders a minimum space check in the PDF document.
     * @param {*} y
     * @param {*} minHeight
     * @param {*} options
     * @returns
     */
    renderAdditionalPageIfNeeded(y, options = {}, minHeight,) {
        if ((this.document.internal.pageSize.height - y) < minHeight) {
            y = this.renderAdditionalPage(y, options);
        }
        return y;
    }

    /**
     * Saves the current font and color settings. Use if you need to do
     * something and then revert the PDF to the way it used to be.
     */
    saveSettings() {
        this.settings.fontSize = this.document.getFontSize();
        this.settings.font = this.document.getFont();
        this.settings.textColor = this.document.getTextColor();
        this.settings.drawColor = this.document.getDrawColor();
        this.settings.fillColor = this.document.getFillColor();
    }

    /**
     * Restores the PDF document settings.
     */
    restoreSettings() {
        this.document.setFontSize(this.settings.fontSize);
        this.document.setFont(
            this.settings.font.fontName,
            this.settings.font.fontStyle,
            0
        );
        this.document.setTextColor(this.settings.textColor);
        this.document.setDrawColor(this.settings.drawColor);
        this.document.setFillColor(this.settings.fillColor);
    }

    /**
     * Renders page numbers in the PDF document
     *
     */
    renderPageNumbers() {
        const documentPages = this.document.getNumberOfPages();
        for (let i = 1; i <= documentPages; i++) {
            this.document.setPage(i);
            this.document.setFontSize(10);
            this.document.setFont(undefined, "normal");
            const pageText = `Page ${i} of ${documentPages}`;
            this.document.text(
                pageText,
                this.document.internal.pageSize.width - this.margin.right,
                this.document.internal.pageSize.height - this.margin.bottom + 50,
                { align: "right" }
            );
        }
    }
}
